import {
  DeleteOutlined,
  EllipsisOutlined,
  EyeOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Alert, Button, Descriptions, Drawer, Dropdown, Empty, Input, Select, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table/interface';
import { useCallback, useMemo, useState } from 'react';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useNotify } from '@/hooks/useNotify';
import {
  useDeleteEnquiryMutation,
  useGetEnquiriesQuery,
  useUpdateEnquiryStatusMutation,
} from '@/modules/enquiries/enquiriesApi';
import {
  ENQUIRY_STATUS_OPTIONS,
  type Enquiry,
  type EnquiryStatus,
} from '@/types/enquiry';
import { getRtkErrorMessage, resolveMutationResult } from '@/utils/apiResponse';
import { confirmDelete } from '@/utils/confirmDialog';
import { formatDate, formatDateTime } from '@/utils/date';
import styles from './Enquiries.module.scss';

function statusTag(status: EnquiryStatus) {
  const colors: Record<EnquiryStatus, string> = {
    new: 'gold',
    in_progress: 'processing',
    quoted: 'blue',
    closed: 'default',
  };
  const label = ENQUIRY_STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status;
  return <Tag color={colors[status]}>{label}</Tag>;
}

function factbooksSummary(factbooks?: Enquiry['factbooks'] | null): string {
  const lines = Array.isArray(factbooks) ? factbooks : [];
  if (!lines.length) return '—';
  const totalCopies = lines.reduce((sum, line) => sum + (Number(line.quantity) || 0), 0);
  if (lines.length === 1) {
    return `${lines[0].volumeName} (${lines[0].quantity} copies)`;
  }
  return `${lines.length} volumes · ${totalCopies} copies`;
}

function displayValue(value?: string | null) {
  const trimmed = value?.trim();
  return trimmed ? trimmed : '—';
}

export default function Enquiries() {
  const notify = useNotify();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [statusFilter, setStatusFilter] = useState<EnquiryStatus | 'all'>('all');
  const [selectedEnquiry, setSelectedEnquiry] = useState<Enquiry | null>(null);

  const hasActiveFilters = search.length > 0 || statusFilter !== 'all';

  const {
    data: listResult,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetEnquiriesQuery({
    page,
    limit,
    search,
    status: statusFilter,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });
  useGlobalLoader(isFetching && !!listResult);

  const [updateStatus] = useUpdateEnquiryStatusMutation();
  const [deleteEnquiry] = useDeleteEnquiryMutation();

  const enquiries = listResult?.results ?? [];
  const total = listResult?.total ?? 0;
  const errorMessage = isError
    ? getRtkErrorMessage(error) ?? 'Failed to load enquiries.'
    : null;

  const applySearch = useCallback(() => {
    setSearch(searchDraft.trim());
    setPage(1);
  }, [searchDraft]);

  const resetFilters = useCallback(() => {
    setSearchDraft('');
    setSearch('');
    setStatusFilter('all');
    setPage(1);
  }, []);

  const handleStatusChange = useCallback(
    async (enquiry: Enquiry, status: EnquiryStatus) => {
      const result = await resolveMutationResult(updateStatus({ id: enquiry.id, body: { status } }));
      if (result.ok) {
        notify.success(result.message ?? 'Status updated.');
        setSelectedEnquiry((current) =>
          current?.id === enquiry.id ? { ...current, status } : current,
        );
        return;
      }
      notify.error(result.message);
    },
    [notify, updateStatus],
  );

  const handleDelete = useCallback(
    async (enquiry: Enquiry) => {
      const confirmed = await confirmDelete(enquiry.institutionName);
      if (!confirmed) return;

      const result = await resolveMutationResult(deleteEnquiry(enquiry.id));
      if (result.ok) {
        notify.success(result.message ?? 'Enquiry deleted.');
        setSelectedEnquiry(null);
        return;
      }
      notify.error(result.message);
    },
    [deleteEnquiry, notify],
  );

  const getRowActionItems = useCallback(
    (enquiry: Enquiry): MenuProps['items'] => [
      {
        key: 'view',
        label: 'View details',
        icon: <EyeOutlined />,
        onClick: () => setSelectedEnquiry(enquiry),
      },
      { type: 'divider' },
      {
        key: 'delete',
        label: 'Delete',
        danger: true,
        icon: <DeleteOutlined />,
        onClick: () => void handleDelete(enquiry),
      },
    ],
    [handleDelete],
  );

  const columns: ColumnsType<Enquiry> = useMemo(
    () => [
      {
        title: 'Institution',
        dataIndex: 'institutionName',
        key: 'institutionName',
        className: styles.primaryCol,
        render: (value: string, record) => (
          <div>
            <span className={styles.primaryCell}>{displayValue(value)}</span>
            <span className={styles.subCell}>{displayValue(record.institutionType)}</span>
          </div>
        ),
      },
      {
        title: 'Contact',
        key: 'contact',
        className: styles.wrapCol,
        render: (_, record) => (
          <div>
            <span className={styles.primaryCell}>{displayValue(record.fullName)}</span>
            <span className={styles.subCell}>{displayValue(record.officialEmail)}</span>
          </div>
        ),
      },
      {
        title: 'Phone',
        dataIndex: 'phone',
        key: 'phone',
        render: (value: string) => <span className={styles.metaCell}>{displayValue(value)}</span>,
      },
      {
        title: 'Location',
        key: 'location',
        className: styles.wrapCol,
        render: (_, record) => (
          <span className={styles.metaCell}>
            {displayValue([record.city, record.state, record.country].filter(Boolean).join(', '))}
          </span>
        ),
      },
      {
        title: 'Formats',
        key: 'formats',
        className: styles.wrapCol,
        render: (_, record) => {
          const formats = Array.isArray(record.formats) ? record.formats : [];
          if (!formats.length) return <span className={styles.metaCell}>—</span>;
          return (
            <span className={styles.metaCell}>
              {formats.slice(0, 2).join(', ')}
              {formats.length > 2 ? ` +${formats.length - 2}` : ''}
            </span>
          );
        },
      },
      {
        title: 'Factbooks',
        key: 'factbooks',
        className: styles.wrapCol,
        render: (_, record) => (
          <span className={styles.metaCell}>{factbooksSummary(record.factbooks)}</span>
        ),
      },
      {
        title: 'Submitted',
        key: 'submittedAt',
        render: (_, record) => {
          const value = record.submittedAt || record.createdAt;
          return (
            <span className={styles.dateCell}>{value ? formatDateTime(value) : '—'}</span>
          );
        },
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        className: styles.statusCol,
        render: (status: EnquiryStatus) => statusTag(status),
      },
      {
        title: 'Action',
        key: 'actions',
        className: styles.actionCol,
        fixed: 'right',
        render: (_, record) => (
          <Dropdown menu={{ items: getRowActionItems(record) }} trigger={['click']}>
            <Button
              type="text"
              size="small"
              icon={<EllipsisOutlined />}
              className={styles.actionBtn}
              onClick={(event) => event.stopPropagation()}
            />
          </Dropdown>
        ),
      },
    ],
    [getRowActionItems],
  );

  const detail = selectedEnquiry;
  const detailFactbooks = Array.isArray(detail?.factbooks) ? detail.factbooks : [];
  const detailFormats = Array.isArray(detail?.formats) ? detail.formats : [];

  return (
    <div className={styles.page}>
      <PageHeader
        actions={
          <div className={styles.headerActions}>
            <Button icon={<ReloadOutlined />} onClick={() => void refetch()}>
              Refresh
            </Button>
            {hasActiveFilters ? <Button onClick={resetFilters}>Reset filters</Button> : null}
          </div>
        }
      />

      {errorMessage ? (
        <Alert
          type="error"
          showIcon
          message="Could not load enquiries"
          description={errorMessage}
          style={{ marginBottom: 16 }}
          action={
            <Button size="small" onClick={() => void refetch()}>
              Retry
            </Button>
          }
        />
      ) : null}

      <div className={styles.filtersBar}>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search institution, contact, email, phone..."
          value={searchDraft}
          onChange={(event) => setSearchDraft(event.target.value)}
          onPressEnter={applySearch}
          className={styles.searchInput}
        />
        <Button type="primary" onClick={applySearch}>
          Search
        </Button>
        <Select
          value={statusFilter}
          onChange={(value) => {
            setStatusFilter(value);
            setPage(1);
          }}
          options={ENQUIRY_STATUS_OPTIONS}
          className={styles.statusSelect}
        />
      </div>

      <div className={styles.tableCard}>
        <Table<Enquiry>
          className={styles.table}
          rowKey={(record) => record.id || record.officialEmail || record.phone}
          size="small"
          loading={isLoading && !listResult}
          columns={columns}
          dataSource={enquiries}
          scroll={{ x: 'max-content' }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  isError
                    ? 'Unable to load enquiries'
                    : hasActiveFilters
                      ? 'No enquiries match your filters'
                      : 'No enquiries yet'
                }
              />
            ),
          }}
          pagination={{
            current: page,
            pageSize: limit,
            total,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50],
            showTotal: (totalCount, [start, end]) => `${start}-${end} of ${totalCount}`,
            size: 'small',
            onChange: (nextPage, nextLimit) => {
              setPage(nextPage);
              setLimit(nextLimit);
            },
          }}
          onRow={(record) => ({
            onClick: () => setSelectedEnquiry(record),
            style: { cursor: 'pointer' },
          })}
        />
      </div>

      <Drawer
        title={detail?.institutionName || 'Enquiry details'}
        open={!!detail}
        onClose={() => setSelectedEnquiry(null)}
        width={720}
      >
        {detail ? (
          <>
            <div className={styles.detailSection}>
              <h4>i. Institution details</h4>
              <Descriptions bordered size="small" column={1}>
                <Descriptions.Item label="Institution name">
                  {displayValue(detail.institutionName)}
                </Descriptions.Item>
                <Descriptions.Item label="Type of institution">
                  {displayValue(detail.institutionType)}
                </Descriptions.Item>
                <Descriptions.Item label="Country">{displayValue(detail.country)}</Descriptions.Item>
                <Descriptions.Item label="State">{displayValue(detail.state)}</Descriptions.Item>
                <Descriptions.Item label="City">{displayValue(detail.city)}</Descriptions.Item>
                <Descriptions.Item label="GSTIN">{displayValue(detail.gstin)}</Descriptions.Item>
              </Descriptions>
            </div>

            <div className={styles.detailSection}>
              <h4>ii. Contact person</h4>
              <Descriptions bordered size="small" column={1}>
                <Descriptions.Item label="Full name">{displayValue(detail.fullName)}</Descriptions.Item>
                <Descriptions.Item label="Designation">
                  {displayValue(detail.designation)}
                </Descriptions.Item>
                <Descriptions.Item label="Official email">
                  {displayValue(detail.officialEmail)}
                </Descriptions.Item>
                <Descriptions.Item label="Phone">{displayValue(detail.phone)}</Descriptions.Item>
              </Descriptions>
            </div>

            <div className={styles.detailSection}>
              <h4>iii. Factbooks &amp; quantities</h4>
              {detailFactbooks.length ? (
                <ul className={styles.factbookList}>
                  {detailFactbooks.map((line, index) => (
                    <li key={`${line.volumeName}-${line.quantity}-${index}`}>
                      {line.volumeName} — <strong>{line.quantity}</strong> copies
                    </li>
                  ))}
                </ul>
              ) : (
                <p className={styles.notes}>No factbook volumes were specified.</p>
              )}
            </div>

            <div className={styles.detailSection}>
              <h4>iv. Format &amp; access</h4>
              {detailFormats.length ? (
                <div className={styles.formatTags}>
                  {detailFormats.map((format) => (
                    <Tag key={format}>{format}</Tag>
                  ))}
                </div>
              ) : (
                <p className={styles.notes}>No formats selected.</p>
              )}
            </div>

            <div className={styles.detailSection}>
              <h4>v. Timeline &amp; notes</h4>
              <Descriptions bordered size="small" column={1}>
                <Descriptions.Item label="Required by">
                  {detail.requiredBy ? formatDate(detail.requiredBy) : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Budget cycle / FY">
                  {displayValue(detail.budgetCycle)}
                </Descriptions.Item>
                <Descriptions.Item label="Submitted">
                  {detail.submittedAt || detail.createdAt
                    ? formatDateTime(detail.submittedAt || detail.createdAt)
                    : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Select
                    value={detail.status}
                    options={ENQUIRY_STATUS_OPTIONS.filter((option) => option.value !== 'all')}
                    onChange={(value) => void handleStatusChange(detail, value)}
                    style={{ width: '100%' }}
                  />
                </Descriptions.Item>
                <Descriptions.Item label="Additional notes or special requirements">
                  {detail.additionalNotes ? (
                    <p className={styles.notes}>{detail.additionalNotes}</p>
                  ) : (
                    '—'
                  )}
                </Descriptions.Item>
              </Descriptions>
            </div>
          </>
        ) : null}
      </Drawer>
    </div>
  );
}
